import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CreatorBadges } from '../components/VerifiedBadge';
import { ImageUpload } from '../components/ImageUpload';
import { toast } from 'sonner';
import { 
  MessageCircle, Heart, Send, Plus, Sparkles, HelpCircle, 
  Lightbulb, PartyPopper, MessagesSquare, Link2, 
  Trash2, Store, Clock, Users, ThumbsUp, Smile, Star, Hand
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_ICONS = {
  'welcome': Hand,
  'show-tell': Sparkles,
  'questions': HelpCircle,
  'tips': Lightbulb,
  'celebrations': PartyPopper,
  'general': MessagesSquare
};

const CHANNEL_COLORS = {
  'welcome': 'bg-gradient-to-r from-pastel-crochet/30 to-pastel-art/30 text-pastel-crochet',
  'show-tell': 'bg-pastel-crochet/30 text-pastel-crochet',
  'questions': 'bg-pastel-art/30 text-pastel-art',
  'tips': 'bg-pastel-stickers/30 text-yellow-600',
  'celebrations': 'bg-pastel-keyrings/30 text-orange-500',
  'general': 'bg-muted text-muted-foreground'
};

// Reaction configuration
const REACTIONS = {
  love: { icon: Heart, label: 'Love', color: 'text-red-500', bgColor: 'bg-red-500/10' },
  helpful: { icon: ThumbsUp, label: 'Helpful', color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  inspiring: { icon: Star, label: 'Inspiring', color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' },
  relatable: { icon: Smile, label: 'Relatable', color: 'text-purple-500', bgColor: 'bg-purple-500/10' }
};

// Reaction Button Component
const ReactionButton = ({ type, count, isActive, onClick }) => {
  const config = REACTIONS[type];
  const Icon = config.icon;
  
  return (
    <button
      onClick={() => onClick(type)}
      className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all ${
        isActive 
          ? `${config.bgColor} ${config.color}` 
          : 'text-muted-foreground hover:bg-muted'
      }`}
      title={config.label}
    >
      <Icon className={`w-4 h-4 ${isActive ? 'fill-current' : ''}`} />
      {count > 0 && <span>{count}</span>}
    </button>
  );
};

// Post Card Component
const PostCard = ({ post, onReact, onDelete, currentUserId, token }) => {
  const [myReactions, setMyReactions] = useState([]);
  const [reactions, setReactions] = useState(post.reactions || {});
  const ChannelIcon = CHANNEL_ICONS[post.channel] || MessagesSquare;

  useEffect(() => {
    if (token) fetchMyReactions();
  }, [post.id, token]);

  const fetchMyReactions = async () => {
    try {
      const res = await fetch(`${API}/community/posts/${post.id}/my-reactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMyReactions(await res.json());
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleReaction = async (type) => {
    const isActive = myReactions.includes(type);
    const result = await onReact(post.id, type, !isActive);
    if (result) {
      if (isActive) {
        setMyReactions(myReactions.filter(r => r !== type));
        setReactions({ ...reactions, [type]: Math.max(0, (reactions[type] || 0) - 1) });
      } else {
        setMyReactions([...myReactions, type]);
        setReactions({ ...reactions, [type]: (reactions[type] || 0) + 1 });
      }
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d`;
    return date.toLocaleDateString();
  };

  const totalReactions = Object.values(reactions).reduce((a, b) => a + b, 0);
  const isShoutout = post.is_shoutout;

  return (
    <div 
      className={`card-brutal p-5 animate-fade-in ${isShoutout ? 'ring-2 ring-pastel-crochet/50 bg-gradient-to-br from-pastel-crochet/5 to-pastel-art/5' : ''}`} 
      data-testid={`post-${post.id}`}
    >
      {/* Shoutout Banner */}
      {isShoutout && (
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-pastel-crochet/20">
          <span className="text-2xl">✨</span>
          <span className="font-heading font-bold text-pastel-crochet">Welcome Shoutout!</span>
          <span className="text-2xl">✨</span>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <Link 
            to={post.store ? `/store/${post.store.id}` : '#'}
            className="w-10 h-10 rounded-full bg-pastel-art/30 flex items-center justify-center font-bold flex-shrink-0"
          >
            {post.user?.avatar ? (
              <img src={post.user.avatar} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              post.user?.name?.[0]?.toUpperCase()
            )}
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{post.user?.name}</span>
              {post.store && (
                <CreatorBadges 
                  isVerified={post.store.is_verified}
                  isFoundingCreator={post.store.is_founding_creator}
                  tier={post.store.tier}
                  size="sm"
                />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${CHANNEL_COLORS[post.channel] || CHANNEL_COLORS['general']}`}>
                <ChannelIcon className="w-3 h-3" />
                {post.channel.replace('-', ' ')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(post.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        {currentUserId === post.user_id && !isShoutout && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(post.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Content */}
      <Link to={`/community/post/${post.id}`}>
        <h3 className={`font-heading font-bold text-lg mb-2 hover:text-pastel-art transition-colors ${isShoutout ? 'text-pastel-crochet' : ''}`}>
          {post.title}
        </h3>
      </Link>
      <p className="text-muted-foreground mb-3 line-clamp-3 whitespace-pre-wrap">{post.content}</p>

      {/* Shoutout Store Link */}
      {isShoutout && post.shoutout_store_id && (
        <Link 
          to={`/store/${post.shoutout_store_id}`}
          className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-pastel-crochet/20 to-pastel-art/20 mb-3 hover:from-pastel-crochet/30 hover:to-pastel-art/30 transition-all border border-pastel-crochet/20"
        >
          <div className="w-12 h-12 rounded-full bg-pastel-crochet/30 flex items-center justify-center">
            <Store className="w-6 h-6 text-pastel-crochet" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold">{post.shoutout_store_name}</p>
            <p className="text-sm text-muted-foreground">Visit their store →</p>
          </div>
          <span className="text-2xl">🎉</span>
        </Link>
      )}

      {/* Images */}
      {post.images?.length > 0 && !isShoutout && (
        <div className={`grid gap-2 mb-3 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {post.images.slice(0, 4).map((img, idx) => (
            <div key={idx} className="rounded-xl overflow-hidden bg-muted aspect-video">
              <img src={img} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}

      {/* Linked Product */}
      {post.product && (
        <Link 
          to={`/product/${post.product.id}`}
          className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 mb-3 hover:bg-muted transition-colors"
        >
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
            {post.product.images?.[0] ? (
              <img src={post.product.images[0]} alt="" className="w-full h-full object-cover" />
            ) : (
              <Store className="w-6 h-6 m-3" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{post.product.name}</p>
            <p className="text-sm text-muted-foreground">${post.product.price?.toFixed(2)}</p>
          </div>
          <Link2 className="w-4 h-4 text-muted-foreground" />
        </Link>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-primary/10 flex-wrap">
        {/* Reactions */}
        {Object.keys(REACTIONS).map((type) => (
          <ReactionButton
            key={type}
            type={type}
            count={reactions[type] || 0}
            isActive={myReactions.includes(type)}
            onClick={handleReaction}
          />
        ))}
        
        <div className="flex-1" />
        
        <Link 
          to={`/community/post/${post.id}`}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          <span>{post.comment_count || 0}</span>
        </Link>
        
        {post.store && (
          <Link 
            to={`/store/${post.store.id}`}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Store className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
};

// Channel Sidebar
const ChannelSidebar = ({ channels, selectedChannel, onSelect }) => {
  return (
    <div className="card-brutal p-4 sticky top-24">
      <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
        <Users className="w-5 h-5" />
        Channels
      </h3>
      <div className="space-y-1">
        <button
          onClick={() => onSelect(null)}
          className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
            !selectedChannel ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
          }`}
        >
          All Posts
        </button>
        {channels.map((channel) => {
          const Icon = CHANNEL_ICONS[channel.id] || MessagesSquare;
          return (
            <button
              key={channel.id}
              onClick={() => onSelect(channel.id)}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                selectedChannel === channel.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="truncate">{channel.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// New Post Dialog
const NewPostDialog = ({ channels, onSubmit, products }) => {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState('show-tell');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState(['']);
  const [productId, setProductId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const success = await onSubmit({
      channel,
      title,
      content,
      images: images.filter(img => img.trim()),
      product_id: productId || null
    });
    
    if (success) {
      setOpen(false);
      setTitle('');
      setContent('');
      setImages(['']);
      setProductId('');
    }
    setSubmitting(false);
  };

  const addImageField = () => setImages([...images, '']);
  const updateImage = (idx, value) => {
    const newImages = [...images];
    newImages[idx] = value;
    setImages(newImages);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-brutal-primary" data-testid="new-post-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share with Fellow Creators</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {channels.map((ch) => {
                  const Icon = CHANNEL_ICONS[ch.id];
                  return (
                    <SelectItem key={ch.id} value={ch.id}>
                      <span className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {ch.name}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your post a title..."
              required
              data-testid="post-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your thoughts with fellow creators..."
              className="min-h-[120px]"
              required
              data-testid="post-content-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Images (optional)</Label>
            <div className="grid grid-cols-3 gap-3">
              {images.map((img, idx) => (
                <ImageUpload
                  key={idx}
                  value={img}
                  onChange={(url) => updateImage(idx, url)}
                  aspectRatio="square"
                  label=""
                />
              ))}
              {images.length < 4 && (
                <button
                  type="button"
                  onClick={addImageField}
                  className="aspect-square rounded-xl border-2 border-dashed border-primary/20 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  <Plus className="w-6 h-6" />
                  <span className="text-xs">Add</span>
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Upload up to 4 images for your post.</p>
          </div>

          {products?.length > 0 && (
            <div className="space-y-2">
              <Label>Link a Product (optional)</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="submit-post-btn">
              {submitting ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Community Page
export default function CommunityPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedChannel = searchParams.get('channel');

  const [channels, setChannels] = useState([]);
  const [posts, setPosts] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [recentShoutouts, setRecentShoutouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_creator) {
      navigate('/');
      return;
    }
    fetchChannels();
    fetchPosts();
    fetchMyProducts();
    fetchRecentShoutouts();
  }, [selectedChannel, user]);

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API}/community/channels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setChannels(await res.json());
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRecentShoutouts = async () => {
    try {
      const res = await fetch(`${API}/shoutouts/recent?limit=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setRecentShoutouts(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const url = selectedChannel 
        ? `${API}/community/posts?channel=${selectedChannel}`
        : `${API}/community/posts`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setPosts(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProducts = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/my-store`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyProducts(data.products || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelectChannel = (channelId) => {
    if (channelId) {
      setSearchParams({ channel: channelId });
    } else {
      setSearchParams({});
    }
  };

  const handleCreatePost = async (data) => {
    try {
      const res = await fetch(`${API}/community/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success('Post shared with the community!');
        fetchPosts();
        return true;
      } else {
        toast.error('Failed to create post');
        return false;
      }
    } catch (e) {
      toast.error('Error creating post');
      return false;
    }
  };

  const handleReact = async (postId, reactionType, shouldAdd) => {
    try {
      if (shouldAdd) {
        const res = await fetch(`${API}/community/posts/${postId}/react`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ reaction_type: reactionType })
        });
        return res.ok;
      } else {
        const res = await fetch(`${API}/community/posts/${postId}/react/${reactionType}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        return res.ok;
      }
    } catch (e) {
      return false;
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const res = await fetch(`${API}/community/posts/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Post deleted');
        setPosts(posts.filter(p => p.id !== postId));
      }
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const currentChannel = channels.find(c => c.id === selectedChannel);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pastel-art/20 border border-pastel-art/30 text-sm font-medium mb-3">
              <Users className="w-4 h-4" />
              Creators Only
            </div>
            <h1 className="text-3xl font-heading font-extrabold flex items-center gap-3">
              <MessagesSquare className="w-8 h-8 text-pastel-art" />
              Creator Community
            </h1>
            <p className="text-muted-foreground mt-1">
              {currentChannel ? currentChannel.description : 'A private space for Gathered Things creators to connect, share, and support each other'}
            </p>
          </div>
          <NewPostDialog channels={channels} onSubmit={handleCreatePost} products={myProducts} />
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <ChannelSidebar 
              channels={channels} 
              selectedChannel={selectedChannel}
              onSelect={handleSelectChannel}
            />
            
            {/* Community Guidelines */}
            <div className="card-brutal p-4 mt-4">
              <h4 className="font-semibold mb-2">Creator Guidelines</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Be kind and supportive</li>
                <li>• Celebrate each other's wins</li>
                <li>• Share tips openly</li>
                <li>• Respect boundaries</li>
                <li>• No self-promo spam</li>
              </ul>
            </div>

            {/* Reaction Legend */}
            <div className="card-brutal p-4 mt-4">
              <h4 className="font-semibold mb-2">Reactions</h4>
              <div className="space-y-2 text-sm">
                {Object.entries(REACTIONS).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${config.color}`} />
                      <span className="text-muted-foreground">{config.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recently Welcomed */}
            {recentShoutouts.length > 0 && (
              <div className="card-brutal p-4 mt-4 bg-gradient-to-br from-pastel-crochet/10 to-pastel-art/10">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <span>✨</span> Recently Welcomed
                </h4>
                <div className="space-y-3">
                  {recentShoutouts.map((shoutout) => (
                    <Link 
                      key={shoutout.id}
                      to={`/store/${shoutout.store_id}`}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-pastel-crochet/20 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-pastel-crochet/30 flex items-center justify-center text-sm font-bold">
                        {shoutout.to_user_name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{shoutout.to_user_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{shoutout.store_name}</p>
                      </div>
                      <span className="text-lg">🎉</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Posts Feed */}
          <div className="md:col-span-3 space-y-4">
            {loading ? (
              <LoadingSpinner text="Loading posts..." />
            ) : posts.length > 0 ? (
              posts.map((post) => (
                <PostCard 
                  key={post.id} 
                  post={post}
                  onReact={handleReact}
                  onDelete={handleDelete}
                  currentUserId={user?.id}
                  token={token}
                />
              ))
            ) : (
              <div className="card-brutal p-12 text-center">
                <MessagesSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-bold text-lg mb-2">Welcome to the Creator Community!</h3>
                <p className="text-muted-foreground mb-4">
                  Be the first to share something with your fellow creators!
                </p>
                <NewPostDialog channels={channels} onSubmit={handleCreatePost} products={myProducts} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
